/**
 * Settings → Support Desk Module: mailboxes, templates, field options, SLA, automation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/api';
import SupportDeskMailboxesTab from './SupportDeskMailboxesTab';
import SupportDeskFieldOptionsTab from './SupportDeskFieldOptionsTab';

interface SupportDeskModuleSettingsTabProps {
  onError?: (msg: string) => void;
}

const SupportDeskModuleSettingsTab: React.FC<SupportDeskModuleSettingsTabProps> = ({ onError }) => {
  const [sub, setSub] = useState(0);
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplEdit, setTplEdit] = useState<any | null>(null);
  const [tplForm, setTplForm] = useState({ name: '', subject: '', body: '', is_active: true });
  const [policies, setPolicies] = useState<any[]>([]);
  const [polLoading, setPolLoading] = useState(false);
  const [polDialog, setPolDialog] = useState(false);
  const [polEdit, setPolEdit] = useState<any | null>(null);
  const [polForm, setPolForm] = useState({
    category: '',
    first_response_minutes: 240,
    resolve_minutes: 2880,
    is_active: true,
  });
  const [rules, setRules] = useState<any[]>([]);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [ruleEdit, setRuleEdit] = useState<any | null>(null);
  const [ruleForm, setRuleForm] = useState({
    category: '',
    assignee_user_id: '',
    ack_template_id: '',
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setTplLoading(true);
      const r = await apiService.getSupportDeskTemplates();
      setTemplates(r.templates || []);
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setTplLoading(false);
    }
  }, [onError]);

  const loadPolicies = useCallback(async () => {
    try {
      setPolLoading(true);
      const r = await apiService.getSupportDeskSlaPolicies();
      setPolicies(r.policies || []);
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to load SLA policies');
    } finally {
      setPolLoading(false);
    }
  }, [onError]);

  const loadRules = useCallback(async () => {
    try {
      setRuleLoading(true);
      const r = await apiService.getSupportDeskAutomationRules();
      setRules(r.rules || []);
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to load automation rules');
    } finally {
      setRuleLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (sub === 1) loadTemplates();
    if (sub === 3) loadPolicies();
    if (sub === 4) {
      loadRules();
      loadTemplates();
    }
  }, [sub, loadTemplates, loadPolicies, loadRules]);

  const saveTemplate = async () => {
    try {
      if (tplEdit) {
        await apiService.updateSupportDeskTemplate(tplEdit.id, tplForm);
      } else {
        await apiService.createSupportDeskTemplate(tplForm);
      }
      setTplDialog(false);
      loadTemplates();
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Save failed');
    }
  };

  const savePolicy = async () => {
    try {
      const payload = {
        ...polForm,
        category: polForm.category.trim() || null,
      };
      if (polEdit) {
        await apiService.updateSupportDeskSlaPolicy(polEdit.id, payload);
      } else {
        await apiService.createSupportDeskSlaPolicy(payload);
      }
      setPolDialog(false);
      loadPolicies();
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Save failed');
    }
  };

  const saveRule = async () => {
    try {
      const payload = {
        category: ruleForm.category.trim() || null,
        assignee_user_id: ruleForm.assignee_user_id || null,
        ack_template_id: ruleForm.ack_template_id || null,
        sort_order: ruleForm.sort_order,
        is_active: ruleForm.is_active,
        trigger: 'on_create',
      };
      if (ruleEdit) {
        await apiService.updateSupportDeskAutomationRule(ruleEdit.id, payload);
      } else {
        await apiService.createSupportDeskAutomationRule(payload);
      }
      setRuleDialog(false);
      loadRules();
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the support desk: IMAP mailbox to import inbound email into tickets, email templates for automation, field options, SLA targets, and create-time automation.
      </Typography>
      <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="Mailboxes" />
        <Tab label="Templates" />
        <Tab label="Field options" />
        <Tab label="SLA policies" />
        <Tab label="Automation" />
      </Tabs>
      {sub === 0 && <SupportDeskMailboxesTab onError={onError} />}
      {sub === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1">Email templates</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => {
                setTplEdit(null);
                setTplForm({ name: '', subject: '', body: '', is_active: true });
                setTplDialog(true);
              }}
            >
              Add
            </Button>
          </Box>
          {tplLoading ? (
            <CircularProgress />
          ) : (
            <TableContainerLite templates={templates} onEdit={(t) => { setTplEdit(t); setTplForm({ name: t.name, subject: t.subject || '', body: t.body, is_active: t.is_active }); setTplDialog(true); }} onDelete={async (id) => { if (!window.confirm('Delete?')) return; await apiService.deleteSupportDeskTemplate(id); loadTemplates(); }} />
          )}
          <Dialog open={tplDialog} onClose={() => setTplDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>{tplEdit ? 'Edit template' : 'New template'}</DialogTitle>
            <DialogContent>
              <TextField label="Name" fullWidth margin="normal" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} disabled={!!tplEdit} />
              <TextField label="Subject" fullWidth margin="normal" value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} />
              <TextField label="Body" fullWidth margin="normal" multiline minRows={6} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} />
              <FormControlLabel control={<Switch checked={tplForm.is_active} onChange={(e) => setTplForm({ ...tplForm, is_active: e.target.checked })} />} label="Active" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTplDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveTemplate}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {sub === 2 && <SupportDeskFieldOptionsTab onError={onError} />}
      {sub === 3 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1">SLA policies</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => {
                setPolEdit(null);
                setPolForm({ category: '', first_response_minutes: 240, resolve_minutes: 2880, is_active: true });
                setPolDialog(true);
              }}
            >
              Add
            </Button>
          </Box>
          {polLoading ? <CircularProgress /> : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>First response (min)</TableCell>
                    <TableCell>Resolve (min)</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.category ?? '(default)'}</TableCell>
                      <TableCell>{p.first_response_minutes}</TableCell>
                      <TableCell>{p.resolve_minutes}</TableCell>
                      <TableCell>{p.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => { setPolEdit(p); setPolForm({ category: p.category || '', first_response_minutes: p.first_response_minutes, resolve_minutes: p.resolve_minutes, is_active: p.is_active }); setPolDialog(true); }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={async () => { if (!window.confirm('Delete?')) return; await apiService.deleteSupportDeskSlaPolicy(p.id); loadPolicies(); }}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          <Dialog open={polDialog} onClose={() => setPolDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{polEdit ? 'Edit SLA policy' : 'New SLA policy'}</DialogTitle>
            <DialogContent>
              <TextField label="Category (blank = default)" fullWidth margin="normal" value={polForm.category} onChange={(e) => setPolForm({ ...polForm, category: e.target.value })} helperText="Matches ticket category; leave empty for catch-all default" />
              <TextField label="First response minutes" type="number" fullWidth margin="normal" value={polForm.first_response_minutes} onChange={(e) => setPolForm({ ...polForm, first_response_minutes: parseInt(e.target.value, 10) || 0 })} />
              <TextField label="Resolve minutes" type="number" fullWidth margin="normal" value={polForm.resolve_minutes} onChange={(e) => setPolForm({ ...polForm, resolve_minutes: parseInt(e.target.value, 10) || 0 })} />
              <FormControlLabel control={<Switch checked={polForm.is_active} onChange={(e) => setPolForm({ ...polForm, is_active: e.target.checked })} />} label="Active" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPolDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={savePolicy}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {sub === 4 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1">Automation (on ticket create)</Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => { setRuleEdit(null); setRuleForm({ category: '', assignee_user_id: '', ack_template_id: '', sort_order: 0, is_active: true }); setRuleDialog(true); }}>Add</Button>
          </Box>
          {ruleLoading ? <CircularProgress /> : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sort</TableCell>
                    <TableCell>Category match</TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>Ack template</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.sort_order}</TableCell>
                      <TableCell>{r.category ?? '(any)'}</TableCell>
                      <TableCell>{r.assignee_user_id ? users.find((u) => u.id === r.assignee_user_id)?.display_name || r.assignee_user_id : '—'}</TableCell>
                      <TableCell>{r.ack_template_id ? templates.find((t) => t.id === r.ack_template_id)?.name || r.ack_template_id : '—'}</TableCell>
                      <TableCell>{r.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => { setRuleEdit(r); setRuleForm({ category: r.category || '', assignee_user_id: r.assignee_user_id || '', ack_template_id: r.ack_template_id || '', sort_order: r.sort_order, is_active: r.is_active }); setRuleDialog(true); }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={async () => { if (!window.confirm('Delete?')) return; await apiService.deleteSupportDeskAutomationRule(r.id); loadRules(); }}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{ruleEdit ? 'Edit rule' : 'New rule'}</DialogTitle>
            <DialogContent>
              <TextField label="Category (blank = any)" fullWidth margin="normal" value={ruleForm.category} onChange={(e) => setRuleForm({ ...ruleForm, category: e.target.value })} />
              <TextField label="Sort order" type="number" fullWidth margin="normal" value={ruleForm.sort_order} onChange={(e) => setRuleForm({ ...ruleForm, sort_order: parseInt(e.target.value, 10) || 0 })} />
              <FormControl fullWidth margin="normal">
                <InputLabel>Assignee</InputLabel>
                <Select value={ruleForm.assignee_user_id} label="Assignee" onChange={(e) => setRuleForm({ ...ruleForm, assignee_user_id: e.target.value as string })}>
                  <MenuItem value="">(none)</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.display_name || u.username}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Acknowledgement template</InputLabel>
                <Select value={ruleForm.ack_template_id} label="Acknowledgement template" onChange={(e) => setRuleForm({ ...ruleForm, ack_template_id: e.target.value as string })}>
                  <MenuItem value="">(none)</MenuItem>
                  {templates.filter((t) => t.is_active).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel control={<Switch checked={ruleForm.is_active} onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })} />} label="Active" />
              <Alert severity="info" sx={{ mt: 2 }}>First matching rule (by sort order) runs on create. Ack email sends if template and requester email are set.</Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRuleDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveRule}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

function TableContainerLite({ templates, onEdit, onDelete }: { templates: any[]; onEdit: (t: any) => void; onDelete: (id: string) => void }) {
  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Subject</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map((t) => (
            <TableRow key={t.id}>
              <TableCell>{t.name}</TableCell>
              <TableCell>{t.subject}</TableCell>
              <TableCell>{t.is_active ? 'Yes' : 'No'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => onEdit(t)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => onDelete(t.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default SupportDeskModuleSettingsTab;
